import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import {
  clearMerchantAuth,
  getMerchantId,
  getMerchantEmail,
  getMerchantName,
} from "./auth";
import {
  LayoutDashboard,
  Package,
  Layers,
  LogOut,
  TrendingUp,
  ShoppingCart,
  BarChart3,
  Store,
  Clock,
  Users,
  Info,
  ChevronRight,
  Wallet,
  ArrowUpRight
} from "lucide-react";
import { 
  BarChart, Bar, AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, CartesianGrid 
} from 'recharts'; 
import { initMerchantSocket, getSocket } from "./socketClient";
import MerchantNotifications from "./MerchantNotifications";
import { useLanguage } from "./LanguageContext";

export default function MerchantDashboard() {
  const navigate = useNavigate();
  const merchantEmail = getMerchantEmail();
  const merchantId = getMerchantId();
  const merchantName = getMerchantName();
  const { language, setLanguage, t } = useLanguage();
  const audioRef = useRef(null); // 👈 Ajoute ceci

  // ---------------------------------------------------------
  // ⛔️ ZONE CRITIQUE : LOGIQUE INTACTE (100% PRÉSERVÉE)
  // ---------------------------------------------------------
  const [stats, setStats] = useState({
    totalRevenue: 0,
    ordersCount: 0,
    monthlyRevenue: 0,
    monthlyOrders: 0,
    productCount: 0,
    avgOrder: 0,
    lastOrders: [],
    topProducts: [],
    graphData: []
  });

  const [loading, setLoading] = useState(true);
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const fetchStats = async () => {
    try {
      const res = await api.get(`/merchant/${merchantId}/stats`);
      setStats(res.data);
    } catch (err) {
      console.error("Erreur stats :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();

    // 🔌 INIT SOCKET
    initMerchantSocket(merchantId);
    const socket = getSocket();
    if (!socket) return;

    socket.on("new_order", (order) => {

      // 🎵 1. DÉCLENCHER LE SON VIA LA RÉFÉRENCE
      if (audioRef.current) {
        // On remet à 0 au cas où une notif joue déjà
        audioRef.current.currentTime = 0; 
        audioRef.current.play().catch(err => console.log("Audio bloqué :", err));
      }
      
      // On incrémente le compteur rouge de la sidebar
      setNewOrdersCount(prev => prev + 1); 

      setStats((prev) => {
        const ordersCount = prev.ordersCount + 1;
        const totalRevenue = prev.totalRevenue + (order.totalAmount || 0); 
        const avgOrder =
          ordersCount > 0
            ? Math.round(totalRevenue / ordersCount)
            : 0;

        return {
          ...prev,
          ordersCount: prev.ordersCount + 1,
          totalRevenue,
          avgOrder,
        };
      });
      fetchStats(); 
    });

    socket.on("order_updated", () => {
      fetchStats();
    });

    return () => {
      socket.off("new_order");
      socket.off("order_updated");
    };
  }, [merchantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen font-bold text-slate-400 bg-[#FAFAFA]">
        {t.loading}
      </div>
    );
  }
  
  const chartData = stats.graphData && stats.graphData.length > 0 ? stats.graphData : [
    { date: 'J-6', sales: 0, revenue: 0 }, { date: 'J-5', sales: 0, revenue: 0 },
    { date: 'J-4', sales: 0, revenue: 0 }, { date: 'J-3', sales: 0, revenue: 0 },
    { date: 'Hier', sales: 0, revenue: 0 }, { date: 'Auj', sales: 0, revenue: 0 }
  ];
  // ---------------------------------------------------------
  // ✅ FIN ZONE CRITIQUE
  // ---------------------------------------------------------

  return (
    <div className={`flex min-h-screen bg-[#FAFAFA] text-slate-800 ${language === 'ar' ? 'font-arabic' : 'font-sans'}`}>
      
      {/* --- SIDEBAR --- */}
      <aside className="sticky top-0 z-20 flex flex-col h-screen px-6 py-8 bg-white border-r border-gray-100 w-72">
        <div className="px-2 mb-8">
            <h2 className="text-xl font-black tracking-tight text-slate-900">Souqi<span className="text-[#f34141]">.</span> Partner</h2>
        </div>

        {/* Profile Card */}
        <div className="flex items-center gap-4 p-4 mb-8 bg-white border border-gray-100 shadow-sm rounded-2xl">
          <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 text-lg font-black text-white bg-[#f34141] rounded-xl shadow-md shadow-red-100">
            {merchantName?.charAt(0).toUpperCase()}
          </div>
          <div className="overflow-hidden">
            <div className="text-sm font-black truncate text-slate-800">
              {merchantName}
            </div>
            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide truncate">
              {t.connected}
            </div>
          </div>
        </div>

        {/* Navigation unique et propre */}
        <nav className="flex flex-col flex-1 gap-2">
          <NavItem active icon={<LayoutDashboard size={20} />} label={t.navDashboard} />
          
          <NavItem 
            icon={<Package size={20} />} 
            label={t.navOrders} 
            onClick={() => navigate("/merchant/orders")} 
            badge={newOrdersCount} 
          />

          <NavItem icon={<Layers size={20} />} label={t.navProducts} onClick={() => navigate("/merchant/products")} />
          <NavItem icon={<BarChart3 size={20} />} label={t.navSubscription} onClick={() => navigate("/merchant/subscription")} />
          <NavItem icon={<Info size={20} />} label={t.navMarketInfo} onClick={() => navigate("/merchant/public-info")} />
          <NavItem icon={<Store size={20} />} label={t.navSettings} onClick={() => navigate("/merchant/settings")} />
        </nav>

        {/* Logout Button */}
        <button
          onClick={() => {
            clearMerchantAuth();
            navigate("/merchant-login");
          }}
          className="flex items-center gap-3 px-4 py-4 mt-6 text-sm font-bold text-red-500 transition-colors duration-200 bg-red-50 hover:bg-red-100 rounded-2xl group"
        >
          <LogOut size={18} className={`transition-transform ${language === 'ar' ? 'group-hover:translate-x-1' : 'group-hover:-translate-x-1'}`} /> 
          {t.logout}
        </button>
      </aside>

      {/* --- MAIN CONTENT --- */}
      <main className="flex-1 p-8 overflow-y-auto">
        
        {/* HEADER */}
        <header className="relative w-full bg-[#f34141] min-h-[12rem] rounded-[2.5rem] p-10 mb-10 shadow-2xl shadow-red-100 flex flex-wrap justify-between items-start gap-4">
          <div className="absolute top-0 right-0 w-64 h-64 -mt-20 -mr-10 overflow-hidden bg-white rounded-full pointer-events-none opacity-5 blur-3xl"></div>
          
          <div className="relative z-10 mt-2 text-white">
            <h1 className="mb-2 text-4xl font-black tracking-tight">{t.dashboardTitle}</h1>
            <p className="text-sm font-medium text-red-100 opacity-90">
              {t.performanceOverview}
            </p>
          </div>

          <div className="relative z-30 flex flex-wrap items-center gap-4">
            {/* SÉLECTEUR DE LANGUE STYLE GLASSMORPHISM */}
            <div className="flex items-center gap-1 p-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-sm">
              <button 
                onClick={() => setLanguage('fr')} 
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${language === 'fr' ? 'bg-white text-[#f34141] shadow-sm' : 'text-white hover:bg-white/10'}`}
              >FR</button>
              <button 
                onClick={() => setLanguage('ar')} 
                className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all ${language === 'ar' ? 'bg-white text-[#f34141] shadow-sm' : 'text-white hover:bg-white/10'}`}
              >AR</button>
            </div>

            <MerchantNotifications merchantId={merchantId} />

            <div className="flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-sm">
              <span className="text-sm font-bold text-white">{merchantName}</span>
              <span className="bg-white text-[#f34141] w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black shadow-sm">
                {merchantName.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        <div className="mx-auto space-y-8 max-w-7xl">
          
          {/* 1. STATS CARDS */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            <StatCard 
              title={t.totalRevenue} 
              value={`${stats.totalRevenue} MRU`} 
              subtext={t.sinceStart}
              icon={<Wallet size={20} className="text-[#f34141]" />}
              accentColor="bg-red-50"
            />
            <StatCard 
              title={t.totalOrders} 
              value={stats.ordersCount} 
              subtext={t.sinceStart}
              icon={<Package size={20} className="text-blue-600" />}
              accentColor="bg-blue-50"
            />
            <StatCard 
              title={t.monthlyRevenue}
              value={`${stats.monthlyRevenue} MRU`}
              subtext={t.currentMonth}
              icon={<TrendingUp size={20} className="text-emerald-600" />}
              accentColor="bg-emerald-50"
              isHighlight
            />
            <StatCard 
              title={t.monthlyOrders}
              value={stats.monthlyOrders}
              subtext={t.currentMonth}
              icon={<Clock size={20} className="text-emerald-600" />}
              accentColor="bg-emerald-50"
            />
          </div>

          {/* 2. ACTION CARDS */}
          <div className="grid h-full grid-cols-1 gap-6 lg:grid-cols-3">
            <ActionCard 
              title={t.manageOrders} 
              subtitle={t.quickAction} 
              icon={<Package size={28} />}
              color="text-[#f34141]"
              bgColor="bg-red-50"
              onClick={() => navigate("/merchant/orders")}
            />
            <ActionCard 
              title={t.addEditCatalog} 
              subtitle={t.catalog} 
              icon={<ShoppingCart size={28} />}
              color="text-blue-600"
              bgColor="bg-blue-50"
              onClick={() => navigate("/merchant/products")}
            />
            
            {/* Carte Conseil Traduite */}
            <div className="bg-white p-8 rounded-[2rem] shadow-sm border border-gray-100 flex flex-col justify-center h-full relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-24 h-24 bg-yellow-400 rounded-full blur-[50px] opacity-20 group-hover:opacity-30 transition-opacity"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-3">
                    <div className="p-1.5 bg-yellow-100 rounded-lg text-yellow-600">
                        <Info size={16} />
                    </div>
                    <h3 className="font-black text-slate-800 uppercase tracking-widest text-[10px]">{t.proTip}</h3>
                </div>
                <p className="text-sm font-bold leading-relaxed text-slate-500">
                  {t.tipText}
                </p>
              </div>
            </div>
          </div>

          {/* 3. BOTTOM SECTION (LISTS) */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            
            {/* Dernières commandes */}
            <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 min-h-[400px] flex flex-col"> 
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-red-50 rounded-xl text-[#f34141]">
                        <Clock size={20} strokeWidth={2.5}/>
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-800">{t.latestOrders}</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.realTime}</p>
                    </div>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-3 pr-2 max-h-[300px] custom-scrollbar"> 
                {!stats.lastOrders || stats.lastOrders.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-300">
                        <Package size={40} className="mb-2 opacity-50"/>
                        <p className="text-sm font-bold">{t.noOrders}</p>
                    </div>
                ) : (
                    stats.lastOrders.map((order, i) => (
                    <div key={i} className="flex items-center justify-between p-4 transition-all bg-[#FAFAFA] border border-transparent hover:border-gray-200 hover:bg-white hover:shadow-lg hover:shadow-gray-100 rounded-2xl group cursor-pointer">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-xs font-black text-slate-400 group-hover:text-[#f34141] transition-colors">
                                {order._id ? order._id.slice(-3) : '...'}
                            </div>
                            <div>
                                <p className="text-sm font-black text-slate-800">{t.client} <span className="font-medium text-slate-400">#{order._id ? order._id.slice(-4) : '...'}</span></p>
                                <p className="text-[10px] font-bold text-slate-400">{new Date(order.createdAt).toLocaleDateString(language === 'ar' ? 'ar-MA' : 'fr-FR', {day: 'numeric', month: 'short'})}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="font-black text-slate-900">{(order.totalAmount ?? order.total ?? 0)}<span className="text-[10px] text-slate-400 ml-1">MRU</span></p>
                            <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-lg ${
                                order.status === 'livree' ? 'bg-blue-50 text-blue-600' : 
                                order.status === 'annulee' ? 'bg-red-50 text-red-500' : 
                                'bg-green-50 text-green-600'
                            }`}>
                            {order.status || t.paid}
                            </span>
                        </div>
                    </div>
                    ))
                )}
              </div>
            </div>

            {/* Top Ventes */}
            <div className="lg:col-span-2 bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 min-h-[400px] flex flex-col"> 
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-slate-100 rounded-xl text-slate-600">
                        <TrendingUp size={20} strokeWidth={2.5}/>
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-800">{t.topSales}</h3>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{t.byVolume}</p>
                    </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto space-y-4 pr-2 max-h-[300px] custom-scrollbar"> 
                {!stats.topProducts || stats.topProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-slate-300">
                        <Layers size={40} className="mb-2 opacity-50"/>
                        <p className="text-sm font-bold">{t.noSalesData}</p>
                    </div>
                ) : (
                    stats.topProducts.map((prod, i) => (
                    <div key={i} className="flex items-center gap-5 p-2">
                        <div className={`flex items-center justify-center flex-shrink-0 w-8 h-8 font-black rounded-lg text-sm ${i === 0 ? 'bg-[#f34141] text-white shadow-lg shadow-red-100' : 'bg-slate-100 text-slate-500'}`}>
                            {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between mb-2">
                                <h4 className="text-sm font-bold truncate text-slate-700">{prod.name}</h4>
                                <span className="text-xs font-black text-slate-900">{prod.totalSold} <span className="text-[9px] text-slate-400 uppercase">{t.units}</span></span>
                            </div>
                            <div className="w-full h-2 overflow-hidden rounded-full bg-gray-50">
                                <div className={`h-full rounded-full ${i === 0 ? 'bg-[#f34141]' : 'bg-slate-300'}`} style={{width: `${100 - (i * 15)}%`}}></div>
                            </div>
                        </div>
                    </div>
                    ))
                )}
              </div>
            </div>
          </div>
        
          {/* 4. CHARTS SECTION */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 min-h-[350px]">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-1">
                        <Users size={18} className="text-slate-400"/>
                        <h3 className="text-sm font-black tracking-widest uppercase text-slate-800">{t.orderVolume}</h3>
                    </div>
                </div>
                <div className="h-[200px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 700}} dy={10} />
                        <Tooltip 
                            contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px -5px rgba(0, 0, 0, 0.1)', fontSize: '12px', fontWeight: 'bold'}}
                            cursor={{fill: '#f8fafc'}}
                        />
                        <Bar dataKey="sales" name={t.sales} fill="#f34141" radius={[6, 6, 6, 6]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
            </div>

            <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100 min-h-[350px]">
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-1">
                        <BarChart3 size={18} className="text-green-500"/>
                        <h3 className="text-sm font-black tracking-widest uppercase text-slate-800">{t.revenue}</h3>
                    </div>
                </div>
                <div className="h-[200px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                        <defs>
                            <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.1}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8', fontWeight: 700}} dy={10} />
                        <Tooltip 
                            contentStyle={{borderRadius: '12px', border: 'none', boxShadow: '0 4px 20px -5px rgba(0, 0, 0, 0.1)', fontSize: '12px', fontWeight: 'bold'}}
                            formatter={(value) => [`${value} MRU`, t.revenue]}
                        />
                        <Area 
                            type="monotone" 
                            dataKey="revenue" 
                            stroke="#10B981" 
                            strokeWidth={3} 
                            fillOpacity={1} 
                            fill="url(#colorRev)" 
                        />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
            </div>
          </div>
        </div>
      </main>
      {/* 🎵 Balise audio cachée dans le DOM */}
      <audio 
        ref={audioRef} 
        src={`${process.env.PUBLIC_URL}/universfield-new-notification-057-494255.mp3`} 
        preload="auto"
      />
    </div>
  );
}

// --- SOUS-COMPOSANTS UI (LÉGÈREMENT ADAPTÉS) ---

function NavItem({ icon, label, active, onClick, badge }) {
  const { language } = useLanguage();
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-4 px-4 py-3.5 rounded-2xl transition-all duration-200 text-sm font-bold w-full relative group ${
        active
          ? "bg-slate-900 text-white shadow-xl shadow-slate-200"
          : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
      }`}
    >
      <span className={active ? "text-[#f34141]" : "text-slate-400"}>{icon}</span>
      <span className={language === 'ar' ? 'mr-auto' : ''}>{label}</span>
      
      {/* Badge Rouge pour les commandes */}
      {badge > 0 && (
        <span className={`
          absolute ${language === 'ar' ? 'left-4' : 'right-4'} 
          flex h-5 min-w-[20px] items-center justify-center rounded-full 
          bg-[#f34141] px-1.5 text-[10px] font-black text-white 
          shadow-lg shadow-red-200 animate-pulse
        `}>
          {badge}
        </span>
      )}
    </button>
  );
}
function StatCard({ title, value, subtext, icon, accentColor, isHighlight }) {
  return (
    <div className={`p-6 rounded-[2rem] border transition-all duration-300 flex flex-col justify-between h-44 group
        ${isHighlight 
            ? 'bg-white border-red-100 shadow-xl shadow-red-50' 
            : 'bg-white border-gray-100 shadow-sm hover:shadow-md'
        }`}>
      <div className="flex items-start justify-between">
        <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase truncate max-w-[70%]">{title}</span>
        <div className={`p-2.5 rounded-xl ${accentColor} transition-transform group-hover:scale-110`}>{icon}</div> 
      </div>
      <div>
        <div className="mb-1 text-2xl font-black tracking-tight truncate text-slate-900">{value}</div>
        <div className="flex items-center gap-1 text-xs font-bold text-slate-400">
             {subtext}
        </div>
      </div>
    </div>
  );
}

function ActionCard({ title, subtitle, icon, color, bgColor, onClick }) {
  const { language } = useLanguage();
  return (
    <button 
      onClick={onClick}
      className="bg-white p-8 rounded-[2rem] shadow-sm border border-gray-100 hover:shadow-xl hover:scale-[1.02] hover:border-red-100 transition-all duration-300 flex flex-col justify-between text-left group h-full relative overflow-hidden"
    >
      <div className="flex items-start justify-between w-full">
         <div className={`p-3.5 rounded-2xl ${bgColor} ${color} mb-6 transition-colors group-hover:bg-[#f34141] group-hover:text-white`}>
            {icon}
         </div>
         <div className={`-mt-2 -mr-2 transition-opacity opacity-0 group-hover:opacity-100 ${language === 'ar' ? 'rotate-180' : ''}`}>
            <div className="p-2 text-white rounded-full bg-slate-900">
                <ArrowUpRight size={16} />
            </div>
         </div>
      </div>
      <div>
        <div className="mb-1 text-[10px] font-black uppercase tracking-widest text-slate-400">{subtitle}</div>
        <div className="text-xl font-black text-slate-800 group-hover:text-[#f34141] transition-colors">{title}</div>
      </div>
    </button>
  );
}

